/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import type { DragMode } from './warningEventTimelineDragUtils';
import { computeDraggedTimes } from './warningEventTimelineDragUtils';

interface DragOrigin {
  mode: DragMode;
  startClientX: number;
  // where the block is drawn, so a block outside the range follows the pointer right away
  fromMs: number;
  untilMs: number;
  // the stored times, to tell whether the drag actually changed anything
  actualFromMs: number;
  actualUntilMs: number;
  msPerPx: number;
}

interface DragPreview {
  from: Date;
  until: Date;
}

export interface UseBlockDragParams {
  from: Date | null;
  until: Date | null;
  rangeStart: Date;
  rangeEnd: Date;
  now: Date;
  maxUntil: Date;
  drawableWidth: number;
  onTimesChange?: (validFrom: string, validUntil: string) => void;
}

export interface UseBlockDragResult {
  dragPreview: DragPreview | null;
  dragMode: DragMode | null;
  beginDrag: (mode: DragMode, event: React.PointerEvent) => void;
  onPointerMove: (event: React.PointerEvent) => void;
  onPointerUp: (event: React.PointerEvent) => void;
  onPointerCancel: (event: React.PointerEvent) => void;
}

const releaseCapture = (event: React.PointerEvent): void => {
  try {
    event.currentTarget.releasePointerCapture(event.pointerId);
  } catch {
    // ignore: capture may already be released
  }
};

export const useBlockDrag = ({
  from,
  until,
  rangeStart,
  rangeEnd,
  now,
  maxUntil,
  drawableWidth,
  onTimesChange,
}: UseBlockDragParams): UseBlockDragResult => {
  const [dragPreview, setDragPreview] = React.useState<DragPreview | null>(
    null,
  );
  const [dragMode, setDragMode] = React.useState<DragMode | null>(null);
  const originRef = React.useRef<DragOrigin | null>(null);
  const previewRef = React.useRef<DragPreview | null>(null);

  const beginDrag = React.useCallback(
    (mode: DragMode, event: React.PointerEvent): void => {
      if (!onTimesChange || !from || !until || drawableWidth <= 0) {
        return;
      }
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      const actualFromMs = from.getTime();
      const actualUntilMs = until.getTime();
      const drawn = computeDraggedTimes({
        mode,
        deltaMs: 0,
        fromMs: actualFromMs,
        untilMs: actualUntilMs,
        rangeStartMs: rangeStart.getTime(),
        rangeEndMs: rangeEnd.getTime(),
        nowMs: now.getTime(),
        maxUntilMs: maxUntil.getTime(),
      });
      originRef.current = {
        mode,
        startClientX: event.clientX,
        fromMs: drawn.fromMs,
        untilMs: drawn.untilMs,
        actualFromMs,
        actualUntilMs,
        msPerPx: (rangeEnd.getTime() - rangeStart.getTime()) / drawableWidth,
      };
      setDragMode(mode);
    },
    [
      onTimesChange,
      from,
      until,
      rangeStart,
      rangeEnd,
      now,
      maxUntil,
      drawableWidth,
    ],
  );

  const endDrag = React.useCallback((): void => {
    originRef.current = null;
    previewRef.current = null;
    setDragMode(null);
    setDragPreview(null);
  }, []);

  const onPointerMove = React.useCallback(
    (event: React.PointerEvent): void => {
      const origin = originRef.current;
      if (!origin) {
        return;
      }
      const deltaMs = (event.clientX - origin.startClientX) * origin.msPerPx;
      const { fromMs, untilMs } = computeDraggedTimes({
        mode: origin.mode,
        deltaMs,
        fromMs: origin.fromMs,
        untilMs: origin.untilMs,
        rangeStartMs: rangeStart.getTime(),
        rangeEndMs: rangeEnd.getTime(),
        nowMs: now.getTime(),
        maxUntilMs: maxUntil.getTime(),
      });
      const preview = { from: new Date(fromMs), until: new Date(untilMs) };
      previewRef.current = preview;
      setDragPreview(preview);
    },
    [rangeStart, rangeEnd, now, maxUntil],
  );

  const onPointerUp = React.useCallback(
    (event: React.PointerEvent): void => {
      const origin = originRef.current;
      if (!origin) {
        return;
      }
      releaseCapture(event);
      const preview = previewRef.current;
      if (preview && onTimesChange) {
        const hasChanged =
          preview.from.getTime() !== origin.actualFromMs ||
          preview.until.getTime() !== origin.actualUntilMs;
        if (hasChanged) {
          onTimesChange(
            preview.from.toISOString(),
            preview.until.toISOString(),
          );
        }
      }
      endDrag();
    },
    [onTimesChange, endDrag],
  );

  const onPointerCancel = React.useCallback(
    (event: React.PointerEvent): void => {
      releaseCapture(event);
      endDrag();
    },
    [endDrag],
  );

  React.useEffect(() => {
    if (!dragMode) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        endDrag();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return (): void => window.removeEventListener('keydown', onKeyDown);
  }, [dragMode, endDrag]);

  return {
    dragPreview,
    dragMode,
    beginDrag,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  };
};
